import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'
import { useInputValidation, getInputClassName } from '@/hooks/useInputValidation'
import useCurrencyStore from '@/stores/currencyStore'

const CurrencyForm = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = !!id

  const {
    currentCurrency,
    loading,
    error,
    fetchCurrencyById,
    createCurrency,
    updateCurrency,
    clearError,
    clearCurrentCurrency
  } = useCurrencyStore()

  // Validation hooks for each field
  const codeValidation = useInputValidation('', {
    required: true,
    pattern: /^[A-Z]{3}$/,
    patternMessage: 'Le code doit être composé de 3 lettres majuscules (ex: EUR, USD)',
    validate: (value) => {
      if (!/^[A-Z]{3}$/.test(value)) {
        return 'Le code doit être composé de 3 lettres majuscules'
      }
      return null
    }
  })

  const symbolValidation = useInputValidation('', {
    required: true,
    minLength: 1,
    maxLength: 5,
    validate: (value) => {
      if (!value.trim()) {
        return 'Le symbole est requis'
      }
      return null
    }
  })

  const nameValidation = useInputValidation('', {
    required: true,
    minLength: 2,
    maxLength: 50,
    validate: (value) => {
      if (!value.trim()) {
        return 'Le nom est requis'
      }
      return null
    }
  })

  const decimalDigitsValidation = useInputValidation('2', {
    required: true,
    pattern: /^[0-8]$/,
    validate: (value) => {
      const num = parseInt(value, 10)
      if (isNaN(num) || num < 0 || num > 8) {
        return 'Le nombre de décimales doit être entre 0 et 8'
      }
      return null
    }
  })

  const roundingValidation = useInputValidation('0', {
    pattern: /^\d+(\.\d+)?$/,
    validate: (value) => {
      const num = parseFloat(value)
      if (isNaN(num) || num < 0) {
        return 'L\'arrondi ne peut pas être négatif'
      }
      return null
    }
  })

  const decimalSeparatorValidation = useInputValidation('.', {
    required: true,
    pattern: /^[.,]$/,
    validate: (value) => {
      if (!value || (value !== '.' && value !== ',')) {
        return 'Le séparateur décimal doit être "." ou ","'
      }
      return null
    }
  })

  const thousandSeparatorValidation = useInputValidation(',', {
    required: true,
    pattern: /^[.,\s']$/,
    validate: (value) => {
      if (!value || !/[.,\s']/.test(value)) {
        return 'Le séparateur de milliers doit être ".", ",", espace ou apostrophe'
      }
      return null
    }
  })

  const [symbolPosition, setSymbolPosition] = useState<'before' | 'after'>('after')
  const [enabled, setEnabled] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isEditMode && id) {
      fetchCurrencyById(id)
    }

    return () => {
      clearCurrentCurrency()
    }
  }, [id, isEditMode])

  useEffect(() => {
    if (isEditMode && currentCurrency) {
      codeValidation.setValue(currentCurrency.code)
      symbolValidation.setValue(currentCurrency.symbol)
      nameValidation.setValue(currentCurrency.name)
      decimalDigitsValidation.setValue(currentCurrency.decimal_digits.toString())
      roundingValidation.setValue(currentCurrency.rounding.toString())
      decimalSeparatorValidation.setValue(currentCurrency.decimal_separator)
      thousandSeparatorValidation.setValue(currentCurrency.thousand_separator)
      setSymbolPosition(currentCurrency.symbol_position)
      setEnabled(currentCurrency.enabled)
    }
  }, [currentCurrency, isEditMode])

  const validateForm = (): boolean => {
    const validations = [
      codeValidation.validate(),
      symbolValidation.validate(),
      nameValidation.validate(),
      decimalDigitsValidation.validate(),
      roundingValidation.validate(),
      decimalSeparatorValidation.validate(),
      thousandSeparatorValidation.validate()
    ]

    return validations.every(v => v)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      alert('Veuillez corriger les erreurs dans le formulaire')
      return
    }

    setSubmitting(true)

    const formData = {
      code: codeValidation.value.toUpperCase(),
      symbol: symbolValidation.value,
      name: nameValidation.value,
      decimal_digits: parseInt(decimalDigitsValidation.value, 10),
      rounding: parseFloat(roundingValidation.value),
      symbol_position: symbolPosition,
      decimal_separator: decimalSeparatorValidation.value,
      thousand_separator: thousandSeparatorValidation.value,
      enabled
    }

    try {
      if (isEditMode && id) {
        await updateCurrency(id, formData)
        alert('Devise mise à jour avec succès')
      } else {
        await createCurrency(formData)
        alert('Devise créée avec succès')
      }
      navigate('/currencies')
    } catch (error: any) {
      console.error('Error saving currency:', error)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading && isEditMode) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 bg-gray-200 animate-pulse rounded" />
        <div className="h-96 w-full bg-gray-200 animate-pulse rounded" />
      </div>
    )
  }

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Devises', href: '/currencies' },
    { label: isEditMode ? 'Modifier la devise' : 'Nouvelle devise' }
  ]

  const handleBack = () => navigate('/currencies')
  const handleSave = () => {
    // Trigger form submission
    const form = document.querySelector('form')
    if (form) {
      form.requestSubmit()
    }
  }

  const actions = [
    {
      label: 'Retour',
      onClick: handleBack,
      variant: 'outline' as const,
      icon: <ArrowLeft className="h-4 w-4" />
    },
    {
      label: isEditMode ? 'Mettre à jour' : 'Créer',
      onClick: handleSave,
      variant: 'default' as const,
      icon: <Save className="h-4 w-4" />,
      disabled: submitting
    }
  ]

  // Format example
  const formatExample = () => {
    const amount = 1234567.89
    const decimalDigits = parseInt(decimalDigitsValidation.value) || 2
    
    // Format the number with the specified decimal digits
    const parts = amount.toFixed(decimalDigits).split('.')
    let integerPart = parts[0]
    const decimalPart = parts[1] || ''
    
    // Apply thousand separator
    if (thousandSeparatorValidation.value) {
      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparatorValidation.value)
    }
    
    // Combine with decimal separator
    let formatted = integerPart
    if (decimalPart) {
      formatted += decimalSeparatorValidation.value + decimalPart
    }
    
    // Add symbol
    if (symbolPosition === 'before') {
      return `${symbolValidation.value || '$'}${formatted}`
    } else {
      return `${formatted}${symbolValidation.value || '$'}`
    }
  }

  return (
    <div className="space-y-6">
      <DetailHeader
        title={isEditMode ? 'Modifier la devise' : 'Nouvelle devise'}
        subtitle={isEditMode ? 'Modifiez les informations de la devise' : 'Créez une nouvelle devise pour le système'}
        breadcrumbs={breadcrumbs}
        actions={actions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-4" onClick={clearError}>
              Fermer
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <DetailSection title="Informations de base">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Code ISO */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Code ISO 4217 <span className="text-red-500">*</span>
                <span className="text-xs text-muted-foreground ml-2">(ex: EUR, USD, CAD)</span>
              </label>
              <Input
                id="code"
                value={codeValidation.value}
                onChange={codeValidation.handleChange}
                onBlur={codeValidation.handleBlur}
                className={getInputClassName(
                  !!codeValidation.error,
                  codeValidation.isValid && codeValidation.touched,
                  'w-full'
                )}
                placeholder="EUR"
                maxLength={3}
                autoComplete="off"
              />
              {codeValidation.error && (
                <p className="text-sm text-red-500 mt-1">{codeValidation.error}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Code à 3 lettres selon la norme ISO 4217
              </p>
            </div>

            {/* Symbole */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Symbole <span className="text-red-500">*</span>
                <span className="text-xs text-muted-foreground ml-2">(ex: €, $, £)</span>
              </label>
              <Input
                id="symbol"
                value={symbolValidation.value}
                onChange={symbolValidation.handleChange}
                onBlur={symbolValidation.handleBlur}
                className={getInputClassName(
                  !!symbolValidation.error,
                  symbolValidation.isValid && symbolValidation.touched,
                  'w-full'
                )}
                placeholder="€"
                maxLength={5}
                autoComplete="off"
              />
              {symbolValidation.error && (
                <p className="text-sm text-red-500 mt-1">{symbolValidation.error}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Symbole monétaire (1-5 caractères)
              </p>
            </div>

            {/* Nom */}
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium">
                Nom complet <span className="text-red-500">*</span>
                <span className="text-xs text-muted-foreground ml-2">(ex: Euro, Dollar US, Yen japonais)</span>
              </label>
              <Input
                id="name"
                value={nameValidation.value}
                onChange={nameValidation.handleChange}
                onBlur={nameValidation.handleBlur}
                className={getInputClassName(
                  !!nameValidation.error,
                  nameValidation.isValid && nameValidation.touched,
                  'w-full'
                )}
                placeholder="Euro"
                autoComplete="off"
              />
              {nameValidation.error && (
                <p className="text-sm text-red-500 mt-1">{nameValidation.error}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Nom complet de la devise
              </p>
            </div>
          </div>
        </DetailSection>

        <DetailSection title="Configuration du format">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Position du symbole */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Position du symbole
              </label>
              <select
                value={symbolPosition}
                onChange={(e) => setSymbolPosition(e.target.value as 'before' | 'after')}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="before">Avant le montant (€10.00)</option>
                <option value="after">Après le montant (10.00€)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Position du symbole par rapport au montant
              </p>
            </div>

            {/* Décimales */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Nombre de décimales <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                id="decimal_digits"
                value={decimalDigitsValidation.value}
                onChange={decimalDigitsValidation.handleChange}
                onBlur={decimalDigitsValidation.handleBlur}
                className={getInputClassName(
                  !!decimalDigitsValidation.error,
                  decimalDigitsValidation.isValid && decimalDigitsValidation.touched,
                  'w-full'
                )}
                min="0"
                max="8"
                step="1"
              />
              {decimalDigitsValidation.error && (
                <p className="text-sm text-red-500 mt-1">{decimalDigitsValidation.error}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Nombre de chiffres après la virgule (0-8)
              </p>
            </div>

            {/* Arrondi */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Arrondi
              </label>
              <Input
                type="number"
                id="rounding"
                value={roundingValidation.value}
                onChange={roundingValidation.handleChange}
                onBlur={roundingValidation.handleBlur}
                className={getInputClassName(
                  !!roundingValidation.error,
                  roundingValidation.isValid && roundingValidation.touched,
                  'w-full'
                )}
                min="0"
                step="0.01"
                placeholder="0.00"
              />
              {roundingValidation.error && (
                <p className="text-sm text-red-500 mt-1">{roundingValidation.error}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Valeur d'arrondi (ex: 0.05 pour arrondir au 5 centimes)
              </p>
            </div>

            {/* Séparateur décimal */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Séparateur décimal <span className="text-red-500">*</span>
              </label>
              <Input
                id="decimal_separator"
                value={decimalSeparatorValidation.value}
                onChange={decimalSeparatorValidation.handleChange}
                onBlur={decimalSeparatorValidation.handleBlur}
                className={getInputClassName(
                  !!decimalSeparatorValidation.error,
                  decimalSeparatorValidation.isValid && decimalSeparatorValidation.touched,
                  'w-full'
                )}
                maxLength={1}
                placeholder="."
              />
              {decimalSeparatorValidation.error && (
                <p className="text-sm text-red-500 mt-1">{decimalSeparatorValidation.error}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Caractère séparant les décimales (généralement "." ou ",")
              </p>
            </div>

            {/* Séparateur de milliers */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Séparateur de milliers <span className="text-red-500">*</span>
              </label>
              <Input
                id="thousand_separator"
                value={thousandSeparatorValidation.value}
                onChange={thousandSeparatorValidation.handleChange}
                onBlur={thousandSeparatorValidation.handleBlur}
                className={getInputClassName(
                  !!thousandSeparatorValidation.error,
                  thousandSeparatorValidation.isValid && thousandSeparatorValidation.touched,
                  'w-full'
                )}
                maxLength={1}
                placeholder=","
              />
              {thousandSeparatorValidation.error && (
                <p className="text-sm text-red-500 mt-1">{thousandSeparatorValidation.error}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Caractère séparant les milliers (généralement "," ou espace)
              </p>
            </div>

            {/* Statut */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Statut
              </label>
              <select
                value={enabled ? 'true' : 'false'}
                onChange={(e) => setEnabled(e.target.value === 'true')}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="true">Activée</option>
                <option value="false">Désactivée</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Une devise désactivée ne sera pas disponible pour sélection
              </p>
            </div>
          </div>
        </DetailSection>

        <DetailSection title="Exemple de formatage">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Exemple avec les paramètres actuels:</p>
            <div className="font-mono text-lg bg-white p-3 rounded border mb-3">
              {formatExample()}
            </div>
            <p className="text-xs text-gray-500">
              Format: {symbolPosition === 'before' ? 'Symbole avant' : 'Symbole après'},
              {decimalDigitsValidation.value || 2} décimales,
              séparateur décimal "{decimalSeparatorValidation.value}",
              séparateur de milliers "{thousandSeparatorValidation.value}"
            </p>
          </div>
        </DetailSection>

        <div className="flex justify-end space-x-4 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={submitting}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={submitting}
          >
            <Save className="mr-2 h-4 w-4" />
            {isEditMode ? 'Mettre à jour' : 'Créer la devise'}
            {submitting && '...'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default CurrencyForm
